import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Report Filter State Interface
 */
export interface ReportFilterState {
  // Current report context
  currentReportType: 'sales' | 'inventory' | 'expense' | 'profit-loss' | 'multi-outlet';
  scope: 'current' | 'all' | 'specific';
  outletId?: string; // Current outlet (user's default)
  storeId?: string; // Current store
  selectedOutletIds: string[]; // For 'specific' scope

  // Date range (pending)
  dateRange: {
    startDate: string | null; // ISO string for serialization
    endDate: string | null; // ISO string for serialization
  };

  // Additional filters (pending)
  filters: {
    [key: string]: any;
  };

  // Applied filters (what's currently active)
  appliedFilters: {
    startDate: string | null;
    endDate: string | null;
    scope: 'current' | 'all' | 'specific';
    outletIds: string[];
    [key: string]: any;
  };
}

// Storage key
const STORAGE_KEY = 'cashierpos_report_filters';

/**
 * Load filters from localStorage
 */
const loadFiltersFromStorage = (): Partial<ReportFilterState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Version check (for future migrations)
    if (parsed.version !== 1) return null;

    // Clean old data (>30 days)
    const savedAt = new Date(parsed.savedAt);
    const now = new Date();
    const daysDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff > 30) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
    return null;
  }
};

/**
 * Save filters to localStorage
 */
const saveFiltersToStorage = (state: ReportFilterState): void => {
  try {
    const toSave = {
      version: 1,
      savedAt: new Date().toISOString(),
      data: {
        currentReportType: state.currentReportType,
        scope: state.scope,
        outletId: state.outletId,
        storeId: state.storeId,
        selectedOutletIds: state.selectedOutletIds,
        dateRange: state.dateRange,
        filters: state.filters,
      },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};

/**
 * Initial state
 */
const getInitialState = (): ReportFilterState => {
  const savedFilters = loadFiltersFromStorage();

  return {
    currentReportType: savedFilters?.currentReportType || 'sales',
    scope: savedFilters?.scope || 'current',
    outletId: savedFilters?.outletId,
    storeId: savedFilters?.storeId,
    selectedOutletIds: savedFilters?.selectedOutletIds || [],
    dateRange: savedFilters?.dateRange || {
      startDate: null,
      endDate: null,
    },
    filters: savedFilters?.filters || {},
    appliedFilters: {
      startDate: null,
      endDate: null,
      scope: 'current',
      outletIds: [],
    },
  };
};

/**
 * Report Filter Slice
 */
export const reportFilterSlice = createSlice({
  name: 'reportFilter',
  initialState: getInitialState(),
  reducers: {
    // Set report type
    setReportType: (state, action: PayloadAction<ReportFilterState['currentReportType']>) => {
      state.currentReportType = action.payload;
      saveFiltersToStorage(state);
    },

    // Set scope
    setScope: (state, action: PayloadAction<ReportFilterState['scope']>) => {
      state.scope = action.payload;
      saveFiltersToStorage(state);
    },

    // Set outlet ID
    setOutletId: (state, action: PayloadAction<string | undefined>) => {
      state.outletId = action.payload;
      saveFiltersToStorage(state);
    },

    // Set store ID
    setStoreId: (state, action: PayloadAction<string | undefined>) => {
      state.storeId = action.payload;
      saveFiltersToStorage(state);
    },

    // Set selected outlets for comparison
    setSelectedOutlets: (state, action: PayloadAction<string[]>) => {
      state.selectedOutletIds = action.payload;
      saveFiltersToStorage(state);
    },

    // Set date range
    setDateRange: (state, action: PayloadAction<{ startDate: string | null; endDate: string | null }>) => {
      state.dateRange = action.payload;
      saveFiltersToStorage(state);
    },

    // Set individual filter
    setFilter: (state, action: PayloadAction<{ key: string; value: any }>) => {
      state.filters[action.payload.key] = action.payload.value;
      saveFiltersToStorage(state);
    },

    // Apply all pending filters
    applyFilters: (state) => {
      state.appliedFilters = {
        startDate: state.dateRange.startDate,
        endDate: state.dateRange.endDate,
        scope: state.scope,
        outletIds: state.scope === 'specific' ? state.selectedOutletIds :
          state.scope === 'current' && state.outletId ? [state.outletId] : [],
        ...state.filters,
      };
      saveFiltersToStorage(state);
    },

    // Reset filters to defaults
    resetFilters: (state) => {
      state.scope = 'current';
      state.selectedOutletIds = [];
      state.dateRange = {
        startDate: null,
        endDate: null,
      };
      state.filters = {};
      state.appliedFilters = {
        startDate: null,
        endDate: null,
        scope: 'current',
        outletIds: [],
      };
      saveFiltersToStorage(state);
    },

    // Load filters from localStorage
    loadFilters: (state) => {
      const saved = loadFiltersFromStorage();
      if (saved) {
        Object.assign(state, saved);
      }
    },

    // Clear localStorage
    clearPersistedFilters: () => {
      localStorage.removeItem(STORAGE_KEY);
    },
  },
  extraReducers: (builder) => {
    // Clear filters on logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      (state) => {
        localStorage.removeItem(STORAGE_KEY);
        Object.assign(state, {
          currentReportType: 'sales',
          scope: 'current',
          outletId: undefined,
          storeId: undefined,
          selectedOutletIds: [],
          dateRange: { startDate: null, endDate: null },
          filters: {},
          appliedFilters: { startDate: null, endDate: null, scope: 'current', outletIds: [] },
        });
      }
    );
  },
});

// Actions
export const {
  setReportType,
  setScope,
  setOutletId,
  setStoreId,
  setSelectedOutlets,
  setDateRange,
  setFilter,
  applyFilters,
  resetFilters,
  loadFilters,
  clearPersistedFilters,
} = reportFilterSlice.actions;

// Selectors
export const selectReportFilter = (state: RootState) => state.reportFilter;
export const selectAppliedFilters = (state: RootState) => state.reportFilter.appliedFilters;
export const selectDateRange = (state: RootState) => state.reportFilter.dateRange;
export const selectScope = (state: RootState) => state.reportFilter.scope;
export const selectSelectedOutlets = (state: RootState) => state.reportFilter.selectedOutletIds;
export const selectCurrentReportType = (state: RootState) => state.reportFilter.currentReportType;

// Check if filters have changed since last apply
export const selectFilterChanged = (state: RootState): boolean => {
  const { dateRange, scope, selectedOutletIds, filters, appliedFilters } = state.reportFilter;

  return (
    dateRange.startDate !== appliedFilters.startDate ||
    dateRange.endDate !== appliedFilters.endDate ||
    scope !== appliedFilters.scope ||
    JSON.stringify(selectedOutletIds) !== JSON.stringify(appliedFilters.outletIds) ||
    JSON.stringify(filters) !== JSON.stringify(
      Object.keys(appliedFilters)
        .filter(key => !['startDate', 'endDate', 'scope', 'outletIds'].includes(key))
        .reduce((acc, key) => ({ ...acc, [key]: appliedFilters[key] }), {})
    )
  );
};

// Get human-readable filter summary
export const selectFilterSummary = (state: RootState): string => {
  const { appliedFilters, scope } = state.reportFilter;
  const parts: string[] = [];

  // Date range
  if (appliedFilters.startDate && appliedFilters.endDate) {
    const start = new Date(appliedFilters.startDate).toLocaleDateString();
    const end = new Date(appliedFilters.endDate).toLocaleDateString();
    parts.push(`${start} - ${end}`);
  }

  // Scope
  if (scope === 'all') {
    parts.push('All outlets');
  } else if (scope === 'specific' && appliedFilters.outletIds.length > 0) {
    parts.push(`${appliedFilters.outletIds.length} outlet(s)`);
  } else {
    parts.push('Current outlet');
  }

  return parts.join(' • ');
};

export default reportFilterSlice.reducer;
